import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { resolveDocTitle } from '../../utils/evaluationPlanDocTitle';
import ModalDocumentActionButtons from '../common/ModalDocumentActionButtons';
import ProgramSlideCanvas from '../program/ProgramSlideCanvas';
import type { EvaluationPlanAttachment, EvaluationPlanAttachmentSlot } from '../../types/evaluationPlan';
import { EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS } from '../../types/evaluationPlan';
import {
  createDefaultEvaluationPlanSheetCanvasDocument,
  EVALUATION_PLAN_SHEET_HEADER_BAND_NORM,
  EVALUATION_PLAN_SHEET_CONTINUATION_TOP_NORM,
  getSheetCanvasPages,
  sheetLayoutSubsetToSlideLayout,
} from '../../types/evaluationPlanSheetCanvas';
import { getSheetCanvasFromAttachment } from '../../utils/evaluationPlanAttachment';
import {
  printEvaluationPlanSheetCanvasFromRoot,
  saveEvaluationPlanSheetCanvasAsPdfFromRoot,
} from '../../utils/evaluationPlanSheetCanvasDocumentExport';

function sanitizeFileToken(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_').slice(0, 48);
}

const EvaluationPlanSheetCanvasViewerModal: React.FC<{
  open: boolean;
  slot: EvaluationPlanAttachmentSlot | null;
  attachment: EvaluationPlanAttachment | null;
  onClose: () => void;
  stackAboveDocumentList?: boolean;
  subjectNames?: string[];
}> = ({ open, slot, attachment, onClose, stackAboveDocumentList = false, subjectNames }) => {
  const exportRootRef = useRef<HTMLDivElement | null>(null);
  const [exportBusy, setExportBusy] = useState(false);

  const documentData = useMemo(() => {
    if (!open || slot !== 'sheet') return null;
    const doc = getSheetCanvasFromAttachment(attachment) ?? createDefaultEvaluationPlanSheetCanvasDocument();
    return { ...doc, title: resolveDocTitle('sheet', doc.title, subjectNames) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slot, attachment, (subjectNames ?? []).join('|')]);

  /** 모든 페이지(1쪽만 머리말 띠) — 보기·인쇄·PDF 가 페이지마다 A4 한 장 */
  const slideLayouts = useMemo(
    () => (documentData ? getSheetCanvasPages(documentData).map((p) => sheetLayoutSubsetToSlideLayout(p)) : []),
    [documentData]
  );
  const slideLayout = slideLayouts[0] ?? null;

  useEffect(() => {
    if (!open) setExportBusy(false);
  }, [open]);

  const slotLabel = slot ? EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS[slot] : '';

  const buildExportFileNameBase = useCallback(() => {
    const titleToken = sanitizeFileToken(documentData?.title?.trim() || '평가지');
    const dateLabel = new Date().toLocaleDateString('ko-KR').replace(/\./g, '').trim();
    return `평가지_${titleToken}_${dateLabel}`;
  }, [documentData?.title]);

  const handleSavePdf = useCallback(async () => {
    const root = exportRootRef.current;
    if (!root || !documentData) return;
    setExportBusy(true);
    try {
      await saveEvaluationPlanSheetCanvasAsPdfFromRoot({
        root,
        fileName: `${buildExportFileNameBase()}.pdf`,
      });
    } catch (error) {
      console.error('평가지 PDF 저장 실패:', error);
      alert('PDF 저장에 실패했습니다.');
    } finally {
      setExportBusy(false);
    }
  }, [buildExportFileNameBase, documentData]);

  const handlePrint = useCallback(async () => {
    const root = exportRootRef.current;
    if (!root || !documentData) return;
    setExportBusy(true);
    try {
      await printEvaluationPlanSheetCanvasFromRoot(root, documentData.title.trim() || `${slotLabel} 웹 문서`);
    } catch (error) {
      console.error('평가지 프린트 실패:', error);
      alert('프린트에 실패했습니다.');
    } finally {
      setExportBusy(false);
    }
  }, [documentData, slotLabel]);

  if (!open || slot !== 'sheet' || !documentData || !slideLayout) return null;

  return (
    <div
      className={
        'cp-modal-backdrop cp-evaluation-plan-web-doc-backdrop cp-evaluation-plan-sheet-canvas-backdrop cp-evaluation-plan-sheet-canvas-viewer-backdrop' +
        (stackAboveDocumentList ? ' cp-evaluation-plan-viewer-backdrop--above-doc-list' : '')
      }
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        className="cp-modal cp-evaluation-plan-web-doc-modal cp-evaluation-plan-sheet-canvas-modal cp-evaluation-plan-sheet-canvas-viewer-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-evaluation-plan-sheet-canvas-viewer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-modal-document-header cp-evaluation-plan-web-doc-viewer-header">
          <h3 id="cp-evaluation-plan-sheet-canvas-viewer-title">{slotLabel} 웹 문서</h3>
          <ModalDocumentActionButtons
            onSavePdf={() => void handleSavePdf()}
            onPrint={() => void handlePrint()}
            busy={exportBusy}
          />
        </div>

        <div className="cp-evaluation-plan-web-doc-full-bleed-line" aria-hidden="true" />

        <div className="cp-modal-body cp-evaluation-plan-sheet-canvas-body">
          <div className="cp-evaluation-plan-sheet-canvas-scroll" ref={exportRootRef}>
            {slideLayouts.map((layout, i) => (
            <div key={i} className="cp-evaluation-plan-sheet-canvas-a4-page">
              <ProgramSlideCanvas
                layout={layout}
                onLayoutChange={() => {}}
                readOnly
                viewMode
                toolbarPreset="evaluation-sheet"
                canvasAspectRatio="a4"
                imageStorageKind="evaluation-sheet"
                fixedHeaderBand={
                  i === 0 ? { title: documentData.title, academicYearLabel: documentData.academicYearLabel } : undefined
                }
                contentBoundsNorm={{
                  top: i === 0 ? documentData.headerBandNorm ?? EVALUATION_PLAN_SHEET_HEADER_BAND_NORM : EVALUATION_PLAN_SHEET_CONTINUATION_TOP_NORM,
                  bottom: 1,
                }}
                defaultTextColorHex={layout.defaultTextColorHex}
                canvasBackgroundColorHex={layout.pageBackgroundColorHex}
              />
            </div>
            ))}
          </div>
        </div>

        <div className="cp-evaluation-plan-web-doc-full-bleed-line" aria-hidden="true" />

        <div className="cp-modal-footer cp-evaluation-plan-web-doc-modal-footer">
          <button type="button" className="cp-btn-primary" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default EvaluationPlanSheetCanvasViewerModal;
